import { mainStore } from '~/stores';
import { isCookieConsentGranted } from '~/utils/cookies';
import { EventDeduplicator } from './EventDeduplicator';
import { EVENT_REGISTRY } from './eventRegistry';
import type { AnalyticsEvent, EventPayloadMap } from './events';
import type { AnalyticsProvider, ProviderName } from './types';

class AnalyticsServiceImpl {
  private providers = new Map<ProviderName, AnalyticsProvider>();
  private batchQueue = new Map<string, Record<string, unknown>[]>();
  private deduplicator = new EventDeduplicator();
  private initialized = false;

  registerProvider(provider: AnalyticsProvider): void {
    this.providers.set(provider.name, provider);
  }

  initialize(): void {
    if (this.initialized) return;
    this.deduplicator.start();
    for (const provider of this.providers.values()) {
      if (provider.isAvailable()) {
        provider.initialize();
      }
    }
    this.initialized = true;
  }

  track<E extends AnalyticsEvent>(
    event: E,
    ...args: EventPayloadMap[E] extends undefined
      ? []
      : [payload: EventPayloadMap[E]]
  ): void {
    const payload = (args[0] ?? {}) as Record<string, unknown>;
    const entry = EVENT_REGISTRY[event];
    if (!entry) {
      return;
    }

    if (this.deduplicator.isDuplicate(event, payload)) {
      return;
    }

    for (const providerName of entry.providers) {
      const provider = this.providers.get(providerName);
      if (!provider || !provider.isAvailable()) continue;

      if (provider.requiresConsent && !this.isConsentGranted()) continue;

      const resolvedEventName =
        entry.eventNames?.[providerName] ?? (event as string);
      const resolvedPayload = entry.transformers?.[providerName]
        ? entry.transformers[providerName]!(payload)
        : payload;

      provider.track(resolvedEventName, resolvedPayload);
    }
  }

  addToBatch(event: AnalyticsEvent, payload: Record<string, unknown>): void {
    const key = event as string;
    const queue = this.batchQueue.get(key) ?? [];
    queue.push(payload);
    this.batchQueue.set(key, queue);
  }

  flush(event: AnalyticsEvent): void {
    const key = event as string;
    const queue = this.batchQueue.get(key);
    if (!queue?.length) return;

    const entry = EVENT_REGISTRY[key];
    if (!entry) return;

    for (const providerName of entry.providers) {
      const provider = this.providers.get(providerName);
      if (!provider || !provider.isAvailable()) continue;
      if (provider.requiresConsent && !this.isConsentGranted()) continue;

      const resolvedEventName =
        entry.eventNames?.[providerName] ?? (event as string);

      if (provider.trackBatch) {
        provider.trackBatch(resolvedEventName, queue);
      } else {
        for (const item of queue) {
          provider.track(resolvedEventName, item);
        }
      }
    }

    this.batchQueue.set(key, []);
  }

  destroy(): void {
    this.deduplicator.destroy();
    for (const provider of this.providers.values()) {
      provider.destroy?.();
    }
    this.providers.clear();
    this.batchQueue.clear();
    this.initialized = false;
  }

  private isConsentGranted(): boolean {
    if (mainStore.isRN) return true;
    return isCookieConsentGranted();
  }
}

export const analyticsService = new AnalyticsServiceImpl();
